export type MomentMessageType =
  | "BASIC_TEXT_MESSAGE"
  | "WIDE_MESSAGE"
  | "WIDE_LIST_MESSAGE"
  | "CAROUSEL_FEED_MESSAGE"
  | "CAROUSEL_COMMERCE_MESSAGE";

export type MomentButton = {
  id: string;
  title: string;
  pcLandingUrl: string;
  mobileLandingUrl: string;
};

export type MomentCoupon = {
  couponBookTitleType: "DISCOUNT_PRICE" | "DISCOUNT_RATE" | "FREE_SHIPPING" | "FREE_GIFT" | "UPGRADE";
  couponBookTitle: string;
  title: string;
  pcLandingUrl: string;
  mobileLandingUrl: string;
};

export type MomentImageAsset = {
  imagePreview: string | null;
  imageName: string | null;
  imageFile: File | null;
  imageUploadedUrl: string;
};

export type MomentListItem = {
  id: string;
  description: string;
  imagePreview: string | null;
  imageName: string | null;
  imageFile: File | null;
  imageUploadedUrl: string;
  pcLandingUrl: string;
  mobileLandingUrl: string;
};

export type MomentCarouselItem = {
  id: string;
  messageTitle: string;
  description: string;
  imagePreview: string | null;
  imageName: string | null;
  imageFile: File | null;
  imageUploadedUrl: string;
  pcLandingUrl: string;
  mobileLandingUrl: string;
  buttonTitle: string;
  buttonPcLandingUrl: string;
  buttonMobileLandingUrl: string;
  secondaryButtonTitle: string;
  secondaryButtonPcLandingUrl: string;
  secondaryButtonMobileLandingUrl: string;
  secondaryButtonEnabled: boolean;
  priceAmount: string;
  discountedPriceAmount: string;
  shareFlag: boolean;
  couponEnabled: boolean;
  coupon: MomentCoupon;
};

export type MomentCommerceIntro = {
  messageTitle: string;
  description: string;
  imagePreview: string | null;
  imageName: string | null;
  imageFile: File | null;
  imageUploadedUrl: string;
  pcLandingUrl: string;
  mobileLandingUrl: string;
};

export type MomentMoreButton = {
  pcLandingUrl: string;
  mobileLandingUrl: string;
};

export type MomentMessageTypeOption = {
  value: MomentMessageType;
  label: string;
  description: string;
  previewTitle: string;
  supportsImage: boolean;
  previewTag?: string;
};

export const MOMENT_MESSAGE_TYPE_OPTIONS: MomentMessageTypeOption[] = [
  {
    value: "BASIC_TEXT_MESSAGE",
    label: "기본 텍스트",
    description: "텍스트 중심의 기본 메시지",
    previewTitle: "기본 텍스트형",
    supportsImage: false,
  },
  {
    value: "WIDE_MESSAGE",
    label: "와이드 이미지",
    description: "이미지와 본문을 함께 노출",
    previewTitle: "와이드형",
    supportsImage: true,
  },
  {
    value: "WIDE_LIST_MESSAGE",
    label: "와이드 아이템 리스트",
    description: "리스트형 상품/콘텐츠 노출",
    previewTitle: "와이드 리스트형",
    supportsImage: true,
    previewTag: "LIST",
  },
  {
    value: "CAROUSEL_FEED_MESSAGE",
    label: "캐러셀 피드",
    description: "여러 장의 피드를 카드형으로 노출",
    previewTitle: "캐러셀 피드형",
    supportsImage: true,
    previewTag: "FEED",
  },
  {
    value: "CAROUSEL_COMMERCE_MESSAGE",
    label: "캐러셀 커머스",
    description: "상품 카드 중심의 캐러셀",
    previewTitle: "캐러셀 커머스형",
    supportsImage: true,
    previewTag: "COMMERCE",
  },
];

export const MOMENT_POLICY_ITEMS = [
  "카카오모먼트 메시지 발송 가능 시간은 08:00 ~ 20:50(한국 시간)입니다.",
  "(광고) 표기 문구는 해제할 수 없습니다.",
  "채널을 차단한 사용자는 발송 대상에서 제외됩니다.",
  "고객사의 광고성 정보 수신 동의 회원 대상 발송은 사전 권한 신청이 필요할 수 있습니다.",
  "URL에 UTF-8로 인코딩되지 않은 한글 또는 특수문자(%, |, “ 등)가 포함되면 iOS 카카오톡 인앱브라우저에서 랜딩이 실패할 수 있습니다.",
  "파라미터 및 매크로 치환이 필요한 딥링크 형식의 URL은 지원하지 않습니다.",
  "와이드 아이템 리스트, 캐러셀 피드, 캐러셀 커머스의 서브아이템 및 카드는 2개 이상이어야합니다."
];

export const MOMENT_SAMPLE_CHANNELS = [
  "비즈톡 (@비즈톡)",
  "모먼트 데모 (@moment-demo)",
];

/** 메시지 레벨 버튼 최대 수 (캐러셀 타입은 카드별 버튼 사용 → 0) */
export function getMaxButtons(type: MomentMessageType): number {
  switch (type) {
    case "BASIC_TEXT_MESSAGE": return 2;
    case "WIDE_MESSAGE": return 1;
    case "WIDE_LIST_MESSAGE": return 1;
    default: return 0;
  }
}

export function createMomentButton(id: string): MomentButton {
  return {
    id,
    title: "",
    pcLandingUrl: "",
    mobileLandingUrl: "",
  };
}

export function createMomentCoupon(): MomentCoupon {
  return {
    couponBookTitleType: "UPGRADE",
    couponBookTitle: "",
    title: "",
    pcLandingUrl: "",
    mobileLandingUrl: "",
  };
}

export function createMomentListItem(id: string): MomentListItem {
  return {
    id,
    description: "",
    imagePreview: null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: "",
    pcLandingUrl: "",
    mobileLandingUrl: "",
  };
}

export function createMomentCarouselItem(id: string): MomentCarouselItem {
  return {
    id,
    messageTitle: "",
    description: "",
    imagePreview: null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: "",
    pcLandingUrl: "",
    mobileLandingUrl: "",
    buttonTitle: "",
    buttonPcLandingUrl: "",
    buttonMobileLandingUrl: "",
    secondaryButtonTitle: "",
    secondaryButtonPcLandingUrl: "",
    secondaryButtonMobileLandingUrl: "",
    secondaryButtonEnabled: false,
    priceAmount: "",
    discountedPriceAmount: "",
    shareFlag: false,
    couponEnabled: false,
    coupon: createMomentCoupon(),
  };
}

export function createMomentImageAsset(): MomentImageAsset {
  return {
    imagePreview: null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: "",
  };
}

export function createMomentCommerceIntro(): MomentCommerceIntro {
  return {
    messageTitle: "",
    description: "",
    imagePreview: null,
    imageName: null,
    imageFile: null,
    imageUploadedUrl: "",
    pcLandingUrl: "",
    mobileLandingUrl: "",
  };
}

export function createMomentMoreButton(): MomentMoreButton {
  return {
    pcLandingUrl: "",
    mobileLandingUrl: "",
  };
}
